import { Link } from "@tanstack/react-router";
import { Twitter, Instagram, Facebook, ShieldCheck, Lock, CreditCard } from "lucide-react";
import bidnovaLogo from "@/assets/bidnova-logo.png";
import { LanguageSelector } from "@/components/language-selector";
import { CurrencySelector } from "@/components/currency-selector";
import { useTranslation } from "@/i18n";

export function SiteFooter() {
  const { t } = useTranslation("navigation");
  return (
    <footer className="relative mt-24 border-t border-white/10 bg-background/60 backdrop-blur-xl">
      <div className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="mx-auto max-w-7xl px-6 sm:px-8 pt-16 pb-10 grid gap-12 md:grid-cols-6 text-sm">
        <div className="md:col-span-2">

          <img
            src={bidnovaLogo}
            alt="BidNova Auctions"
            width={1600}
            height={544}
            loading="lazy"
            className="h-10 w-auto"
          />
          <p className="mt-5 text-sm text-muted-foreground max-w-sm leading-relaxed">
            {t("footer.tagline", "The UK's auction marketplace for trading cards, collectibles, fashion and more. Live auctions in GBP, protected end-to-end by Stripe and a fully UK GDPR-compliant platform.")}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <LanguageSelector variant="footer" className="-ml-3" />
            <CurrencySelector variant="footer" />
          </div>

          <div className="mt-6 flex items-center gap-3">
            <a href="https://twitter.com" aria-label="Twitter" target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl hover:border-primary/40 hover:bg-white/10 transition-all duration-200">
              <Twitter className="h-4 w-4" />
            </a>
            <a href="https://instagram.com" aria-label="Instagram" target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl hover:border-primary/40 hover:bg-white/10 transition-all duration-200">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="https://facebook.com" aria-label="Facebook" target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl hover:border-primary/40 hover:bg-white/10 transition-all duration-200">
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4 font-semibold">{t("footer.marketplace", "Marketplace")}</p>
          <ul className="space-y-3">
            <li><Link to="/auctions" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.browseAuctions", "Browse auctions")}</Link></li>
            <li><Link to="/trends" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.marketTrends", "Market trends")}</Link></li>
            <li><Link to="/registry" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.registry", "Verified Card Registry")}</Link></li>
            <li><Link to="/collections" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.collections", "Collections")}</Link></li>
            <li><Link to="/sell" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.sellCard", "Sell a card")}</Link></li>
          </ul>
          <p className="mt-8 text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4 font-semibold">{t("footer.community", "Community")}</p>
          <ul className="space-y-3">
            <li><Link to="/community" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.communityHub", "Community Hub")}</Link></li>
            <li><Link to="/community/articles" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.articles", "News & Articles")}</Link></li>
            <li><Link to="/community/events" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.events", "UK Card Shows")}</Link></li>
            <li><Link to="/community/dealers" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.dealers", "Dealer Directory")}</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4 font-semibold">{t("footer.categories", "Popular Categories")}</p>
          <ul className="space-y-3">
            {[
              ["Pokémon Cards", "pokemon"],
              ["One Piece Cards", "one piece"],
              ["Yu-Gi-Oh! Cards", "yu-gi-oh"],
              ["Magic Cards", "magic the gathering"],
              ["Sports Cards", "sports"],
              ["Topps Cards", "topps"],
              ["Dragon Ball Cards", "dragon ball"],
              ["Gundam Cards", "gundam"],
              ["Disney Lorcana", "lorcana"],
              ["Marvel Cards", "marvel"],
              ["Star Wars Cards", "star wars"],
              ["Digimon Cards", "digimon"],
            ].map(([label, q]) => (
              <li key={label}>
                <a href={`/auctions?q=${encodeURIComponent(q)}`} className="text-foreground/80 hover:text-primary transition-colors">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>



        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4 font-semibold">{t("footer.legal", "Legal")}</p>
          <ul className="space-y-3">
            <li><Link to="/legal/terms" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.terms", "Terms & Conditions")}</Link></li>
            <li><Link to="/legal/privacy" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.privacy", "Privacy")}</Link></li>
            <li><Link to="/legal/cookies" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.cookies", "Cookies")}</Link></li>
            <li><Link to="/legal/buyer-terms" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.buyerTerms", "Buyer Terms")}</Link></li>
            <li><Link to="/legal/seller-agreement" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.sellerAgreement", "Seller Agreement")}</Link></li>
            <li><Link to="/legal/refunds" className="text-foreground/80 hover:text-primary transition-colors">{t("footer.refunds", "Refunds & Disputes")}</Link></li>
            <li>
              <button
                type="button"
                onClick={() => (window as unknown as { openCookieSettings?: () => void }).openCookieSettings?.()}
                className="text-foreground/80 hover:text-primary transition-colors"
              >
                {t("footer.cookieSettings", "Cookie settings")}
              </button>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4 font-semibold">{t("footer.contact", "Contact")}</p>
          <ul className="space-y-3 text-foreground/80">
            <li>
              <a href="mailto:support@bidnovaauctions.uk" className="hover:text-primary transition-colors">
                support@bidnovaauctions.uk
              </a>
            </li>
            <li className="text-muted-foreground">{t("footer.registered", "Registered in England & Wales")}</li>
          </ul>

          <p className="mt-8 text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4 font-semibold">{t("footer.trusted", "Trusted & Secure")}</p>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 backdrop-blur-xl px-2.5 py-1 text-[11px] text-foreground/80">
              <ShieldCheck className="h-3 w-3 text-emerald-400" /> {t("footer.stripeSecured", "Stripe Secured")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 backdrop-blur-xl px-2.5 py-1 text-[11px] text-foreground/80">
              <Lock className="h-3 w-3 text-primary" /> {t("footer.gdpr", "GDPR")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 backdrop-blur-xl px-2.5 py-1 text-[11px] text-foreground/80">
              <CreditCard className="h-3 w-3 text-primary" /> {t("footer.cards", "Card & Apple Pay")}
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-6 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>{t("footer.rights", "© 2026 BidNova Auctions. All rights reserved.")}</p>
          <p>{t("footer.governed", "Governed by the laws of England & Wales · Prices in GBP (£)")}</p>
        </div>
      </div>
    </footer>
  );
}
